import { isPublicRoute } from "@src/config/routes"
import type { SessionResult } from "@src/types/session"
import { logger } from "@src/utils/logger"
import { assign, fromCallback, fromPromise, setup } from "xstate"

export interface BootstrapContext {
  pathname: string
  searchRedirect: string | null
  address: string | null
  chainType: string | null
  sessionResult: SessionResult | null
  unsafeWallet: boolean
  routeIntent: string | null
  _restored: boolean
}

export type BootstrapEvent =
  | {
      type: "WALLET_STATE"
      address: string | null
      chainType: string | null
      restoreStatus: "restoring" | "settled"
    }
  | { type: "AUTH_INVALIDATED" }
  | { type: "SAFETY_CLEAR" }
  | { type: "SAFETY_UNSAFE" }
  | { type: "BANNED_DETECTED" }
  | { type: "VERIFICATION_COMPLETE" }
  | { type: "SIGN_OUT" }
  | {
      type: "PATHNAME_CHANGED"
      pathname: string
      searchRedirect?: string | null
    }

export const appBootstrapMachine = setup({
  types: {} as {
    context: BootstrapContext
    events: BootstrapEvent
    input: { pathname: string }
  },
  actors: {
    authInvalidationListener: fromCallback(({ sendBack }) => {
      if (typeof window === "undefined") return
      const handler = () => sendBack({ type: "AUTH_INVALIDATED" })
      window.addEventListener("auth:invalidated", handler)
      return () => window.removeEventListener("auth:invalidated", handler)
    }),
    checkSession: fromPromise(
      async (_: {
        input: { address: string; chainType: string }
      }): Promise<SessionResult> => {
        throw new Error("checkSession must be provided via .provide()")
      }
    ),
  },
  guards: {
    isPublicRoute: ({ context }) => isPublicRoute(context.pathname),
    isAuthorized: ({ context }) =>
      context.sessionResult?.status === "authorized",
    needsVerification: ({ context }) => {
      const s = context.sessionResult?.status
      return s === "unauthenticated" || s === "reauth_required"
    },
    noWalletOnProtectedRoute: ({ context }) =>
      context.address == null && !isPublicRoute(context.pathname),
    noWalletOnPublicRoute: ({ context }) =>
      context.address == null && isPublicRoute(context.pathname),
    isSessionResultRetryable: (_, params: { result: SessionResult }) =>
      params.result.status === "retryable_failure",
    // Wallet state transition guards
    isFirstRestore: ({ context, event }) =>
      !context._restored &&
      event.type === "WALLET_STATE" &&
      event.restoreStatus === "settled",
    isDisconnect: ({ context, event }) =>
      context._restored &&
      context.address != null &&
      event.type === "WALLET_STATE" &&
      event.address == null,
    isDisconnectOnPublicRoute: ({ context, event }) =>
      context._restored &&
      context.address != null &&
      event.type === "WALLET_STATE" &&
      event.address == null &&
      isPublicRoute(context.pathname),
    isAuthorizedOnLogin: ({ context }) =>
      context.sessionResult?.status === "authorized" &&
      context.pathname === "/login",
    needsVerificationOnLogin: ({ context }) => {
      const s = context.sessionResult?.status
      return (
        (s === "unauthenticated" || s === "reauth_required") &&
        context.pathname === "/login"
      )
    },
    isConnect: ({ context, event }) =>
      context._restored &&
      context.address == null &&
      event.type === "WALLET_STATE" &&
      event.address != null,
    isAddressChange: ({ context, event }) =>
      context._restored &&
      context.address != null &&
      event.type === "WALLET_STATE" &&
      event.address != null &&
      event.address !== context.address,
    isPathnameLogin: ({ event }) =>
      event.type === "PATHNAME_CHANGED" && event.pathname === "/login",
    isPathnameNotLogin: ({ event }) =>
      event.type === "PATHNAME_CHANGED" && event.pathname !== "/login",
    isPathnameProtected: ({ event }) =>
      event.type === "PATHNAME_CHANGED" && !isPublicRoute(event.pathname),
    isPathnameNotLoginAndNeedsVerification: ({ context, event }) => {
      const s = context.sessionResult?.status
      return (
        event.type === "PATHNAME_CHANGED" &&
        event.pathname !== "/login" &&
        (s === "unauthenticated" || s === "reauth_required")
      )
    },
  },
  actions: {
    setWalletInfo: assign(
      (_, params: { address: string | null; chainType: string | null }) => ({
        address: params.address,
        chainType: params.chainType,
      })
    ),
    setWalletInfoFromEvent: assign(({ event }) => {
      if (event.type !== "WALLET_STATE") return {}
      return { address: event.address, chainType: event.chainType }
    }),
    markRestored: assign({ _restored: () => true }),
    setSessionResult: assign((_, params: { result: SessionResult }) => ({
      sessionResult: params.result,
    })),
    setUnsafeWallet: assign({ unsafeWallet: () => true }),
    clearUnsafeWallet: assign({ unsafeWallet: () => false }),
    captureRouteIntent: assign({
      routeIntent: ({ context }) =>
        isPublicRoute(context.pathname) ? null : context.pathname,
    }),
    clearRouteIntent: assign({ routeIntent: () => null }),
    clearWalletState: assign({
      address: () => null,
      chainType: () => null,
      sessionResult: () => null,
      unsafeWallet: () => false,
    }),
    logRestoreTimeout: () => {
      logger.warn("Wallet restore timed out after RESTORE_TIMEOUT delay")
    },
    // Injected via .provide() in AppBootstrapProvider
    navigateToLogin: () => {},
    navigateFromLoginIfNeeded: () => {},
    navigateToBanned: () => {},
    refreshRouter: () => {},
    clearSession: () => {},
  },
  delays: {
    RESTORE_TIMEOUT: 10_000,
    RETRY_SESSION_SHORT: 5_000,
    LEAVING_LOGIN_TIMEOUT: 3_000,
    SESSION_EXPIRY: ({ context }) => {
      if (context.sessionResult?.status !== "authorized") return 60_000
      const msUntilExpiry = context.sessionResult.expiresAt - Date.now()
      // Re-check 30s before expiry, minimum 10s
      return Math.max(msUntilExpiry - 30_000, 10_000)
    },
  },
}).createMachine({
  // RULE: No post-connect UI (verification dialog, account screen, etc.) may
  // render while pathname is /login. All paths out of awaiting_safety that
  // would show UI MUST route through leaving_login first when on /login.
  // leaving_login holds the splash, navigates to /home, and only transitions
  // to the target state (ready or needs_verification) after PATHNAME_CHANGED
  // confirms we've left /login.
  id: "app-bootstrap",
  initial: "booting",
  context: ({ input }) => ({
    pathname: input.pathname,
    searchRedirect: null,
    address: null,
    chainType: null,
    sessionResult: null,
    unsafeWallet: false,
    routeIntent: null,
    _restored: false,
  }),

  invoke: {
    id: "authListener",
    src: "authInvalidationListener",
  },

  on: {
    // Global: keep pathname in sync on every client-side navigation
    PATHNAME_CHANGED: {
      actions: assign({
        pathname: ({ event }) => event.pathname,
        searchRedirect: ({ event }) => event.searchRedirect ?? null,
      }),
    },
    // Global: a 401/auth error was detected — re-check session.
    AUTH_INVALIDATED: {
      target: ".checking_session",
    },
    // Global: wallet disconnect — public routes stay in public_idle,
    // protected routes redirect to login. Overridden in booting.
    WALLET_STATE: [
      {
        guard: "isDisconnectOnPublicRoute",
        target: ".public_idle",
        actions: ["clearWalletState", "clearSession"],
      },
      {
        guard: "isDisconnect",
        target: ".redirecting_to_login",
        actions: ["clearWalletState", "clearSession"],
      },
    ],
  },

  states: {
    booting: {
      on: {
        AUTH_INVALIDATED: {}, // No-op: wallet not restored yet
        WALLET_STATE: [
          {
            guard: "isFirstRestore",
            target: "checking_session",
            actions: ["markRestored", "setWalletInfoFromEvent"],
          },
          // All other WALLET_STATE events ignored during boot
        ],
      },
      after: {
        RESTORE_TIMEOUT: {
          target: "checking_session",
          actions: ["logRestoreTimeout", "markRestored"],
        },
      },
    },

    checking_session: {
      always: [
        {
          guard: "noWalletOnProtectedRoute",
          target: "redirecting_to_login",
          actions: "captureRouteIntent",
        },
        {
          guard: "noWalletOnPublicRoute",
          target: "public_idle",
        },
      ],
      invoke: {
        src: "checkSession",
        // Safe: `always` guards above redirect when address is null before invoke starts
        input: ({ context }) => ({
          address: context.address as string,
          chainType: context.chainType as string,
        }),
        onDone: [
          {
            guard: {
              type: "isSessionResultRetryable",
              params: ({ event }) => ({ result: event.output }),
            },
            target: "retrying_session",
            actions: {
              type: "setSessionResult",
              params: ({ event }) => ({ result: event.output }),
            },
          },
          {
            target: "awaiting_safety",
            actions: {
              type: "setSessionResult",
              params: ({ event }) => ({ result: event.output }),
            },
          },
        ],
        onError: {
          target: "retrying_session",
        },
      },
      on: {
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
      },
    },

    retrying_session: {
      after: {
        RETRY_SESSION_SHORT: {
          target: "checking_session",
          reenter: true,
        },
      },
      on: {
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
      },
    },

    awaiting_safety: {
      on: {
        SAFETY_CLEAR: [
          {
            guard: "isAuthorizedOnLogin",
            target: "leaving_login",
          },
          {
            guard: "needsVerificationOnLogin",
            target: "leaving_login",
          },
          {
            guard: "isAuthorized",
            target: "ready",
          },
          {
            guard: "needsVerification",
            target: "needs_verification",
          },
        ],
        SAFETY_UNSAFE: {
          actions: "setUnsafeWallet",
        },
        BANNED_DETECTED: {
          target: "blocked_banned",
        },
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
      },
    },

    needs_verification: {
      on: {
        VERIFICATION_COMPLETE: {
          target: "checking_session",
        },
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
      },
    },

    public_idle: {
      on: {
        WALLET_STATE: {
          guard: "isConnect",
          target: "checking_session",
          actions: "setWalletInfoFromEvent",
        },
        PATHNAME_CHANGED: {
          guard: "isPathnameProtected",
          target: "redirecting_to_login",
          actions: assign({
            pathname: ({ event }) => event.pathname,
            searchRedirect: ({ event }) => event.searchRedirect ?? null,
            routeIntent: ({ event }) => event.pathname,
          }),
        },
      },
    },

    ready: {
      entry: ["refreshRouter"],
      after: {
        SESSION_EXPIRY: {
          target: "checking_session",
          reenter: true,
        },
      },
      on: {
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
        BANNED_DETECTED: {
          target: "blocked_banned",
        },
        WALLET_STATE: {
          guard: "isAddressChange",
          target: "checking_session",
          actions: ["clearUnsafeWallet", "setWalletInfoFromEvent"],
        },
        // Stale router navigation to /login arriving after the machine already
        // moved past redirecting_to_login (wallet connected mid-redirect).
        PATHNAME_CHANGED: {
          guard: "isPathnameLogin",
          target: "leaving_login",
          actions: assign({
            pathname: ({ event }) => event.pathname,
            searchRedirect: ({ event }) => event.searchRedirect ?? null,
          }),
        },
      },
    },

    leaving_login: {
      entry: "navigateFromLoginIfNeeded",
      after: {
        LEAVING_LOGIN_TIMEOUT: [
          { guard: "needsVerification", target: "needs_verification" },
          { target: "ready" },
        ],
      },
      on: {
        PATHNAME_CHANGED: [
          {
            guard: "isPathnameNotLoginAndNeedsVerification",
            target: "needs_verification",
            actions: assign({
              pathname: ({ event }) => event.pathname,
              searchRedirect: ({ event }) => event.searchRedirect ?? null,
            }),
          },
          {
            guard: "isPathnameNotLogin",
            target: "ready",
            actions: assign({
              pathname: ({ event }) => event.pathname,
              searchRedirect: ({ event }) => event.searchRedirect ?? null,
            }),
          },
        ],
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
      },
    },

    redirecting_to_login: {
      entry: "navigateToLogin",
      on: {
        AUTH_INVALIDATED: {},
        PATHNAME_CHANGED: {
          guard: "isPathnameLogin",
          target: "public_idle",
          actions: assign({
            pathname: ({ event }) => event.pathname,
            searchRedirect: ({ event }) => event.searchRedirect ?? null,
          }),
        },
        WALLET_STATE: {
          guard: "isConnect",
          target: "checking_session",
          actions: "setWalletInfoFromEvent",
        },
      },
    },

    blocked_banned: {
      entry: "navigateToBanned",
      on: {
        SIGN_OUT: {
          target: "redirecting_to_login",
          actions: ["clearRouteIntent"],
        },
      },
    },
  },
})
